import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { showErrorData } from '@/lib/toast'
import { useReviewAction } from '../hooks/use-review-action'
import type { ReviewAction, ReviewPayload } from '../types/review.types'

const DEFAULT_APPROVE_NOTE = '审核通过，内容符合规范'
const MAX_NOTE_LENGTH = 200

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  engineId: string | null
  currentIndex?: number
  totalCount?: number
  onPrev?: () => void
  onNext?: () => void
  onSuccess?: () => void
}

export function EngineReviewDialog({
  open,
  onOpenChange,
  engineId,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
  onSuccess,
}: Props) {
  const review = useReviewAction()
  const [action, setAction] = useState<ReviewAction>('approve')
  const [note, setNote] = useState(DEFAULT_APPROVE_NOTE)
  const [noteDirty, setNoteDirty] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 弹窗打开时重置表单，行为符合预期
    setAction('approve')
    setNote(DEFAULT_APPROVE_NOTE)
    setNoteDirty(false)
    setError(null)
  }, [open, engineId])

  const trimmedNote = note.trim()
  const canSubmit = trimmedNote.length >= 1 && trimmedNote.length <= MAX_NOTE_LENGTH

  const handleActionChange = (value: ReviewAction) => {
    setAction(value)
    if (!noteDirty) {
      setNote(value === 'approve' ? DEFAULT_APPROVE_NOTE : '')
    }
  }

  const handleNoteChange = (value: string) => {
    setNote(value)
    setNoteDirty(true)
    if (error) setError(null)
  }

  const showNav = totalCount !== undefined && totalCount > 1
  const position =
    showNav && currentIndex !== undefined ? currentIndex + 1 : null

  const handleSubmit = (andNext = false) => {
    if (!engineId) return
    if (!canSubmit) {
      setError('请填写 1~200 字的审核备注')
      return
    }

    const payload: ReviewPayload = {
      action,
      note: trimmedNote,
    }

    review.review(
      { engineId, ...payload },
      {
        onSuccess: () => {
          onSuccess?.()
          if (andNext && onNext) {
            onNext()
          } else {
            onOpenChange(false)
          }
        },
        onError: (err) => {
          showErrorData('审核失败', err)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('sm:max-w-lg')}>
        <DialogHeader>
          <div className='flex items-center justify-between'>
            <DialogTitle>审核</DialogTitle>
            {showNav && position !== null && (
              <div className='flex items-center gap-1'>
                <button
                  type='button'
                  onClick={onPrev}
                  disabled={!onPrev || review.isPending}
                  className='hover:bg-muted disabled:opacity-30 rounded p-1'
                  title='上一个'
                >
                  <ChevronLeft className='h-4 w-4' />
                </button>
                <span className='text-muted-foreground min-w-[3rem] text-center text-xs tabular-nums'>
                  {position} / {totalCount}
                </span>
                <button
                  type='button'
                  onClick={onNext}
                  disabled={!onNext || review.isPending}
                  className='hover:bg-muted disabled:opacity-30 rounded p-1'
                  title='下一个'
                >
                  <ChevronRight className='h-4 w-4' />
                </button>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-2'>
            <div className='text-sm font-medium'>审核结果</div>
            <div className='grid grid-cols-2 gap-3'>
              <button
                type='button'
                onClick={() => handleActionChange('approve')}
                aria-pressed={action === 'approve'}
                className={cn(
                  'flex items-center justify-between rounded-lg border px-4 py-3 text-sm font-medium transition',
                  action === 'approve'
                    ? 'border-emerald-500/50 bg-emerald-50 text-emerald-700'
                    : 'hover:bg-muted'
                )}
              >
                <span>通过</span>
                <CheckCircle className='h-5 w-5' />
              </button>
              <button
                type='button'
                onClick={() => handleActionChange('reject')}
                aria-pressed={action === 'reject'}
                className={cn(
                  'flex items-center justify-between rounded-lg border px-4 py-3 text-sm font-medium transition',
                  action === 'reject'
                    ? 'border-destructive/50 bg-destructive/10 text-destructive'
                    : 'hover:bg-muted'
                )}
              >
                <span>拒绝</span>
                <XCircle className='h-5 w-5' />
              </button>
            </div>
          </div>

          <div className='space-y-2'>
            <div className='text-sm font-medium'>备注</div>
            <Textarea
              value={note}
              onChange={(event) => handleNoteChange(event.target.value)}
              placeholder={action === 'reject' ? '请填写拒绝原因' : '请输入审核备注'}
              maxLength={MAX_NOTE_LENGTH}
              className={cn('min-h-28')}
            />
            <div
              className={cn(
                'flex items-center justify-between text-xs',
                error ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              <span>{error ?? '备注需填写 1~200 字'}</span>
              <span>{note.length}/{MAX_NOTE_LENGTH}</span>
            </div>
          </div>
        </div>

        <DialogFooter className='flex-wrap gap-2 sm:flex-nowrap'>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={review.isPending}
            className='sm:mr-auto'
          >
            取消
          </Button>
          {onNext && (
            <Button
              variant='secondary'
              onClick={() => handleSubmit(true)}
              disabled={!engineId || review.isPending}
            >
              {review.isPending ? '提交中…' : '提交并下一个'}
            </Button>
          )}
          <Button
            onClick={() => handleSubmit(false)}
            disabled={!engineId || review.isPending}
          >
            {review.isPending ? '提交中…' : '提交'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
